/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LinearProgress, Paper, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { OrderStats, OrphanedMonthBucket } from '../models/order-stats.type'
import { useGetOrderStatsQuery } from '../orders.service'
import { OrderFilters } from '../order.slice'

const styles = {
    paper: css`
        padding: 16px 16px 0 16px;
        margin-bottom: 16px;
        cursor: pointer;
        :hover,
        :focus {
            background: #efefef;
        }
    `,
    contentPaper: css`
        padding: 16px 16px 0 16px;
        margin-bottom: 16px;
        margin-top: 16px;
    `,
    wrapper: css`
        display: flex;
        flex-wrap: wrap;
    `,
    block: css`
        margin-right: 40px;
        margin-bottom: 16px;
    `,
    label: css`
        color: rgba(0, 0, 0, 1);
        font-size: 12px;
    `,
    arrow: css`
        flex-grow: 1;
        text-align: right;
        align-self: center;
    `,
    heading: css`
        font-size: 16px;
        margin: 16px;
        text-transform: uppercase;
    `,
    value: css`
        font-size: 16px;
        font-weight: 600;
        white-space: nowrap;
        padding-right: 20px;
    `,
    valueAlert: css`
        font-size: 16px;
        font-weight: 600;
        white-space: nowrap;
        padding-right: 20px;
        color: #d32f2f;
    `,
    labelRow: css`
        display: flex;
        align-items: center;
        gap: 4px;
    `,
    infoIcon: css`
        font-size: 14px;
        color: rgba(0, 0, 0, 0.54);
        cursor: help;
    `,
    tooltipBody: css`
        font-size: 12px;
        line-height: 1.5;
    `,
    tooltipBreakdown: css`
        margin-top: 8px;
        display: grid;
        grid-template-columns: auto auto;
        column-gap: 12px;
        row-gap: 2px;
    `,
    tooltipMonth: css`
        opacity: 0.85;
    `,
    tooltipCount: css`
        text-align: right;
        font-weight: 600;
    `,
}
type Props = {
    filters: OrderFilters
}

const OrphanedTooltip: React.FC<{ buckets: OrphanedMonthBucket[] }> = ({ buckets }) => {
    const { t } = useTranslation()
    return (
        <div css={styles.tooltipBody}>
            <div>
                {t(
                    'TEXT_ORPHANED_ORDERS_HELP',
                    'Orphaned orders are unbilled orders dated before the start of the current month. They were never invoiced and are stranded — usually a sign something went wrong with billing.',
                )}
            </div>
            {buckets.length > 0 && (
                <div css={styles.tooltipBreakdown}>
                    {buckets.map((b) => (
                        <React.Fragment key={b.month}>
                            <div css={styles.tooltipMonth}>{b.month}</div>
                            <div css={styles.tooltipCount}>{b.count}</div>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    )
}

const Content: React.FC<{ data?: OrderStats }> = ({ data }) => {
    const { t } = useTranslation()
    const orphaned = data?.orphanedCount ?? 0
    const buckets = data?.orphanedByMonth ?? []
    return (
        <div css={styles.wrapper}>
            <div css={styles.block}>
                <div css={styles.label}>{t('total_billable_reports', 'Total billable count')}</div>
                <div css={styles.value}>{data?.itemsCount ?? 0}</div>
            </div>
            <div css={styles.block}>
                <div css={styles.label}>{t('TEXT_SAME_DAY_EXEMPTIONS', 'Same day exemptions')}</div>
                <div css={styles.value}>{data?.sameDayExemptions ?? 0}</div>
            </div>
            <div css={styles.block}>
                <div css={styles.labelRow}>
                    <div css={styles.label}>{t('TEXT_ORPHANED_ORDERS', 'Orphaned orders')}</div>
                    <Tooltip arrow placement="top" title={<OrphanedTooltip buckets={buckets} />}>
                        <InfoOutlinedIcon css={styles.infoIcon} fontSize="inherit" />
                    </Tooltip>
                </div>
                <div css={orphaned > 0 ? styles.valueAlert : styles.value}>{orphaned}</div>
            </div>
        </div>
    )
}
export const AdminOrdersOverview: React.FC<Props> = ({ filters }) => {
    const { data, isLoading } = useGetOrderStatsQuery({ page: 0, ...filters })
    return (
        <div>
            {isLoading && <LinearProgress />}
            <Paper tabIndex={0} elevation={0} css={styles.contentPaper}>
                <Content data={data} />
            </Paper>
        </div>
    )
}
